/* -----------------Globals--------------- */
import { Link, Stack, router, useLocalSearchParams } from "expo-router";

import { ChooseCategoryContainer } from "../screens/ChooseCategoryContainer";
/* -----------------Child components--------------- */
import { ChooseExamContainer } from "../screens/ChooseExamContainer";
import { RedeemContainer } from "../screens/RedeemContainer";

import type { OnboardingDataItem } from "@configs/custom-app-config-schema";
import { events, pageViewTypes } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppTheme } from "@hooks/useAppTheme";
import useClerkUser from "@hooks/useClerkUser";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/src/components/Box";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import { setProfileProperties, trackCustomEvent } from "analytics";
import useDiagnostic from "containers/useDiagnostic";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { setGoal } from "../api/onboarding";
import AuthButton from "./AuthButton";
import FreeQuizSessionDialog from "./dialogs/FreeQuizSessionDialog";
import OnboardingAppBar from "./onboarding/OnboardingAppBar";
import OnboardingFeatures from "./onboarding/OnboardingFeatures";
import OnboardingGoal from "./onboarding/OnboardingGoal";
import OnboardingHeader from "./onboarding/OnboardingHeader";
import OnboardingProductDemo from "./onboarding/OnboardingProductDemo";
import OnboardingSkills from "./onboarding/OnboardingSkills";
import OnboardingTestDate from "./onboarding/OnboardingTestDate";
import OnboardingWelcome from "./onboarding/OnboardingWelcome";
const MAX_CONTENT_WIDTH = 480;

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const OnboardingRouteContainer = () => {
	const { step: stepString } = useLocalSearchParams<{
		step: string;
		selectedCategory: string;
	}>();
	const step = stepString ? Number.parseInt(stepString, 10) : 0;

	const onboardingSteps = useAppStore((state) => state.onboarding) || [];
	const appName = useAppStore((state) => state.app?.name) || "";
	const trademark = useAppStore((state) => state.app?.trademark) || "";
	const showDiagnostic = useAppStore((state) => state.app?.showDiagnostic);
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const {
		currentExamContext: { organizationId, projectId },
	} = useExamContext();
	const { userPublicMetadataId: userId } = useClerkUser();
	const appId = useAppStore((state) => state.app?.id) || "";

	const { isCreatingSession } = useDiagnostic();

	const [showFreeQuizDialog, setShowFreeQuizDialog] = useState(false);

	const {
		updateExamDate,
		currentExamContext: { examDate },
	} = useExamContext();

	const { t } = useTranslation();
	const stepsToShow = isMobile
		? onboardingSteps.filter((step) =>
				step.isMobile != null ? step.isMobile : true,
			)
		: onboardingSteps;

	const currentStepData = stepsToShow[step];

	const eventParams = useMemo(
		() => ({
			type: pageViewTypes.onboarding,
		}),
		[],
	);
	const { explicitCallTrackCustomEvent } = useAnalytics(
		eventParams,
		!step || step === 0,
	);
	useEffect(() => {
		if (currentStepData) {
			trackCustomEvent({
				eventName: events.onboardingStepStarted,
				step: currentStepData.step,
				description: t(currentStepData.description!),
				title: t(currentStepData.title!),
			});
		}
	}, [currentStepData, t]);

	const showSkip = !!(step && currentStepData?.step !== "features");
	const goalOnboardingStep = stepsToShow.find(
		(step) => step.step === "set-goal",
	);

	const isDemoStep = currentStepData?.step === "demo";
	const isGoalStep = currentStepData?.step === "set-goal";
	const { bottom: safeAreaInsetsBottom } = useSafeAreaInsets();
	const demoOnboardingStep = stepsToShow.find((step) => step.step === "demo");
	const welcomeOnboardingStep = stepsToShow.find(
		(step) => step.step === "welcome",
	);

	const [selectedGoal, setSelectedGoal] = useState<
		OnboardingDataItem | undefined
	>(goalOnboardingStep?.data?.[0]);

	const handleBackPress = () => {
		trackCustomEvent({
			eventName: events.onboardingStepNavPrev,
			step: currentStepData?.step,
			description: t(currentStepData?.description!),
			title: t(currentStepData?.title!),
		});
		if (step > 0) {
			router.setParams({
				step: step ? step - 1 : 0,
			});
		}
	};

	const onSelectGoal = async (goal: OnboardingDataItem) => {
		setSelectedGoal(goal);

		const key = `${organizationId}:${projectId}:${appId}:${userId}:goal`;
		await setGoal({ key, goal: goal.type });
	};

	useEffect(() => {
		if (isGoalStep && selectedGoal) {
			setProfileProperties({
				mem_goal: selectedGoal.type,
			});
			trackCustomEvent({
				eventName: events.onboardingGoalSelected,
				goal: selectedGoal.type,
				description: t(selectedGoal.description!),
				title: t(selectedGoal.title!),
			});
		}
	}, [isGoalStep, selectedGoal, t]);

	const handleSkip = () => {
		explicitCallTrackCustomEvent({
			eventName: events.onboardingStepNavSkipped,
			goal: selectedGoal?.type,
			step: currentStepData?.step,
			description: t(currentStepData?.description!),
			title: t(currentStepData?.title!),
		});
		router.replace("/home");
	};

	const handleNext = (param?: string) => {
		if (step === stepsToShow.length - 1) {
			if (!showDiagnostic) {
				router.replace("/home");
				return;
			}
			try {
				setShowFreeQuizDialog(true);
			} catch (_error) {
				router.replace("/home");
			}
			return;
		}

		explicitCallTrackCustomEvent({
			eventName: events.onboardingStepCompleted,
			step: currentStepData?.step,
			description: t(currentStepData?.description!),
			title: t(currentStepData?.title!),
		});
		explicitCallTrackCustomEvent({
			eventName: events.onboardingStepNavNext,
			step: currentStepData?.step,
			description: t(currentStepData?.description!),
			title: t(currentStepData?.title!),
		});

		router.setParams({
			step: step ? step + 1 : 1,
			param,
		});
	};

	const comps: Record<string, React.ReactNode> = {
		chooseContext: <ChooseExamContainer handleNext={handleNext} />,
		redeem: <RedeemContainer handleNext={handleNext} />,
		chooseCategory: <ChooseCategoryContainer handleNext={handleNext} />,
		welcome: welcomeOnboardingStep ? (
			<OnboardingWelcome welcomeOnboardingStep={welcomeOnboardingStep} />
		) : null,
		"set-goal": goalOnboardingStep?.data ? (
			<OnboardingGoal
				goals={goalOnboardingStep.data}
				selectedGoal={selectedGoal}
				onSelectGoal={onSelectGoal}
			/>
		) : null,
		"set-date": (
			<OnboardingTestDate
				testDate={
					examDate
						? examDate
						: DateTime.now().plus({ months: 1 }).toFormat("MMMM dd, yyyy")
				}
				onDateChange={updateExamDate}
			/>
		),
		examDate: (
			<OnboardingTestDate
				testDate={
					examDate
						? examDate
						: DateTime.now().plus({ months: 1 }).toFormat("MMMM dd, yyyy")
				}
				onDateChange={updateExamDate}
			/>
		),
		skills: <OnboardingSkills />,
		features: <OnboardingFeatures />,
	};

	return (
		<>
			<Stack.Screen
				options={{
					headerShown: true,
					header: () => {
						return (
							<OnboardingAppBar
								onBackAction={step ? handleBackPress : undefined}
								showProgress={!!step}
								progress={step ? step / (stepsToShow.length - 1) : 0}
								maxWidth={MAX_CONTENT_WIDTH - 48}
								rightAction={
									showSkip ? (
										<Link
											href="/home"
											style={{ marginHorizontal: 16 }}
											onPress={handleSkip}
										>
											<Text
												style={{
													color: theme.colors.primary,
													textTransform: "uppercase",
													letterSpacing: 1,
												}}
												variant="bodyLarge"
											>
												{t("onboarding.actions.skip")}
											</Text>
										</Link>
									) : null
								}
							/>
						);
					},
				}}
			/>
			{demoOnboardingStep && isDemoStep ? (
				<OnboardingProductDemo
					onNext={handleNext}
					demoUrl={demoOnboardingStep.demo?.desktop || ""}
				/>
			) : (
				<ScrollContainer
					maxWidth="xs"
					containerProps={{
						containerStyle: {
							padding: 24,
							justifyContent: isMobile ? "space-between" : "flex-start",
							paddingBottom: safeAreaInsetsBottom + 24,
							flexGrow: 1,
							gap: 48,
						},
					}}
				>
					<Box gap={36}>
						<OnboardingHeader
							title={t(currentStepData.title, {
								appName: appName,
							})}
							center={currentStepData.step === "welcome"}
							description={
								isMobile && currentStepData.descriptionMobile
									? t(currentStepData?.descriptionMobile!)
									: t(currentStepData?.description!, {
											trademark: trademark,
										})
							}
						/>
						<Box>{comps[currentStepData?.step]}</Box>
					</Box>
					<Box gap={10} flexDirection={isMobile ? "column-reverse" : "row"}>
						{currentStepData.secondaryCta && (
							<AuthButton
								mode="outlined"
								onPress={handleSkip}
								style={{ flexGrow: 1 }}
							>
								{t(currentStepData.secondaryCta || "")}
							</AuthButton>
						)}
						<AuthButton
							onPress={() => handleNext()}
							fullWidth={!isMobile}
							disabled={isCreatingSession}
							style={{ flexGrow: 1 }}
						>
							{t(currentStepData.cta || "")}
						</AuthButton>
					</Box>
				</ScrollContainer>
			)}

			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={() => {
						setShowFreeQuizDialog(false);
						router.replace("/home");
					}}
				/>
			)}
		</>
	);
};

export default OnboardingRouteContainer;
